import {Direction} from '@angular/cdk/bidi';
import {booleanAttribute, Directive, effect, inject, input, model, OnDestroy, OnInit, output, TemplateRef} from '@angular/core';
import {DcColorType} from '@shared-types';
import {DeviceService} from '@shared/device';

import {DialogControlService} from '../..';
import {DialogModel} from '../../dialog.types';

/**
 * @example:
 * <ng-template DcDialog="dialogId"
 *    height="auto"
 *    width="50%"
 *    maxheight="95vh"
 *    [hasBackdrop]="true"
 *    [mobileDialogDims]="['100%', 'auto', '98vw', '75vh']"
 *
 *    // to use data in template: {user:User, name:string}
 *    let-user="user"
 *    let-name="name
 * >
 * //// dialog content
 * </ng-template>
 */
/**
 * @description [width , height, maxWidth (optional), maxHeight (optional), minWidth (optional), minHeight (optional)]
 */
type MobileDialogDims = [
	width: string | 'full' | undefined,
	height: string | 'full' | undefined,
	maxWidth?: string | 'full' | undefined,
	maxHeight?: string | 'full' | undefined,
	minWidth?: string | 'full' | undefined,
	minHeight?: string | 'full' | undefined,
];

const FullMobileDimsMap = {
	width: '98vw',
	maxWidth: '98vw',
	minWidth: '98vw',
	height: '100%',
	maxHeight: '100%',
	minHeight: '100%',
} as const;

@Directive({
	selector: '[DcDialog]',
	exportAs: 'DcDialog',
	standalone: true,
})
export class DialogDirective implements OnInit, OnDestroy {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	public readonly dialogControlService = inject(DialogControlService);
	public readonly templateRef = inject(TemplateRef);
	private deviceService: DeviceService = inject(DeviceService);
	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	readonly id = input.required<string>({alias: 'DcDialog'});

	readonly panelClass = input(undefined, {transform: (value: string | string[]) => (Array.isArray(value) ? value : value.words())});
	readonly hasBackdrop = input(undefined, {transform: booleanAttribute});
	readonly backdropclass = input<string[], string | string[]>(undefined, {transform: (value: string | string[]) => (Array.isArray(value) ? value : value.words())});
	readonly disableclose = input<boolean>(undefined);

	// Dimensions
	readonly height = model<string | number>(undefined);
	readonly width = model<string | number>(undefined);
	readonly maxHeight = model<string | number>(undefined);
	readonly maxWidth = model<string | number>(undefined);
	readonly minHeight = model<string | number>(undefined);
	readonly minWidth = model<string | number>(undefined);

	readonly closeonnavigation = input<boolean>(true);

	readonly direction = input<Direction>(undefined);

	readonly borderColor = input<DcColorType>(DcColorType.Primary);
	readonly containerClass = input(undefined, {transform: (value: string | string[]) => (Array.isArray(value) ? value : value.words())});
	readonly zIndex = input<number>(undefined);

	/** [width , height, maxWidth (optional), maxHeight (optional), minWidth (optional), minHeight (optional)] */
	readonly mobileDialogDims = input<MobileDialogDims>(undefined);
	/* ------------------------------------------------  Outputs ------------------------------------------------ */
	readonly OnOpen = output<unknown>();
	readonly OnClosed = output<unknown>();
	/* ------------------------------------------------  Signals ------------------------------------------------ */

	/* -------------------------------------------------- Data -------------------------------------------------- */

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {
		const registerDialogEffect = effect(() => {
			const dialogId = this.id();
			if (dialogId) {
				const dialogModel: DialogModel = new DialogModel(dialogId, this, this.templateRef, undefined);
				this.dialogControlService.registerDialog(dialogModel);
				registerDialogEffect.destroy();
			}
		});

		effect(() => {
			if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
				const mobileDimsArray = this.mobileDialogDims();
				if (mobileDimsArray) {
					const mobileDims = {
						width: mobileDimsArray[0],
						height: mobileDimsArray[1],
						maxWidth: mobileDimsArray[2],
						maxHeight: mobileDimsArray[3],
						minWidth: mobileDimsArray[4],
						minHeight: mobileDimsArray[5],
					};
					Object.entries(mobileDims).forEach(([key, value]) => {
						if (value === 'full') mobileDims[key] = FullMobileDimsMap[key];
					});
					this.width.set(mobileDims.width);
					this.height.set(mobileDims.height);
					this.maxWidth.set(mobileDims.maxWidth);
					this.maxHeight.set(mobileDims.maxHeight);
					this.minWidth.set(mobileDims.minWidth);
					this.minHeight.set(mobileDims.minHeight);
				}
			}
		});
	}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */
	ngOnInit(): void {}
	ngOnDestroy(): void {}
	/* ------------------------------------------------  Methods ------------------------------------------------ */
	public open(): void {
		this.dialogControlService.open(this.id());
	}
	public close<D = any>(data?: D): void {
		this.dialogControlService.close(this.id(), data);
	}
}
